import { useEffect, useMemo, useRef, useState } from "react";
import { debounce } from "@mui/material/utils";

export function useGoogleMapsScript() {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;

    if (window.google?.maps?.places) {
      setLoaded(true);
      return;
    }

    const existingScript = document.getElementById("google-maps");

    if (existingScript) {
      existingScript.onload = () => setLoaded(true);
      return;
    }

    const script = document.createElement("script");
    script.id = "google-maps";
    script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY}&libraries=places`;
    script.async = true;

    script.onload = () => setLoaded(true);

    document.head.appendChild(script);
  }, []);

  return loaded;
}

export function useGooglePlacesAutocomplete(inputValue) {
  const autocompleteService = useRef(null);
  const [options, setOptions] = useState([]);

  const fetchPredictions = useMemo(
    () =>
      debounce((request, callback) => {
        autocompleteService.current.getPlacePredictions(request, callback);
      }, 400),
    [],
  );

  useEffect(() => {
    if (!window.google?.maps?.places) return;

    if (!autocompleteService.current) {
      autocompleteService.current =
        new window.google.maps.places.AutocompleteService();
    }

    if (!inputValue) {
      setOptions([]);
      return;
    }

    fetchPredictions(
      {
        input: inputValue,
        componentRestrictions: { country: "in" },
      },
      (results) => {
        setOptions(results || []);
      },
    );
  }, [inputValue, fetchPredictions]);

  return options;
}

export function usePlaceGeocode() {
  const getCoordinates = (placeId) => {
    return new Promise((resolve, reject) => {
      const geocoder = new window.google.maps.Geocoder();

      geocoder.geocode({ placeId }, (results, status) => {
        if (status !== "OK" || !results[0]) {
          reject(status);
          return;
        }

        const location = results[0].geometry.location;

        resolve({
          lat: location.lat(),
          lng: location.lng(),
          raw: results,
        });
      });
    });
  };

  return { getCoordinates };
}
